import {
  getSiloDimensionsFromCurves,
  normalizeSiloLastProfile,
  profileMetricsFromLevel,
  shouldUseLevelFromSettings,
} from './siloLevelToKg';

export function findFoodByPk(pk, foodByCompany = []) {
  if (!pk || !Array.isArray(foodByCompany) || foodByCompany.length === 0) {
    return null;
  }
  return foodByCompany.find(food => food.pk === pk) || null;
}

export function getPlateHeightFromSettings(siloSettings) {
  const value = siloSettings?.[0]?.settings?.plateHeight;
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : 0.9;
}

export function getHasLowerConeFromSettings(siloSettings) {
  const value = siloSettings?.[0]?.settings?.has_lower_cone
    ?? siloSettings?.[0]?.settings?.hasLowerCone;
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  return undefined;
}

export function getUseLevelFromSettings(siloSettings) {
  return shouldUseLevelFromSettings(siloSettings);
}

/** Same weight/bags rules as SiloCard (level below 0.1% is treated as empty). */
export function getSiloDisplayWeightAndBags(silo) {
  const level = Number(silo?.level) || 0;
  if (level < 0.1) {
    return { weight: 0, bags: 0 };
  }
  return {
    weight: Number(silo?.weight) || 0,
    bags: Number(silo?.bags) || 0,
  };
}

/**
 * Maps a silo profile API object to the shape expected by SiloCard (same as home page).
 */
export function transformSiloProfileForCard(
  siloData,
  {
    sensorId,
    siloPk,
    sacaWeight = 60,
    foodByCompany = [],
    siloNameFallback,
    siloSettings,
    informedMaxWeight,
  } = {}
) {
  if (!siloData) return null;

  const profile = normalizeSiloLastProfile(siloData);
  const food = findFoodByPk(profile.food, foodByCompany);
  const useLevelMetrics = shouldUseLevelFromSettings(siloSettings);
  const level = profile.level || 0;
  const density = food?.density
    || (profile.weight && profile.volume ? profile.weight / profile.volume : 0);

  let volume = profile.volume || 0;
  let weight = profile.weight || 0;

  if (useLevelMetrics) {
    const fromLevel = profileMetricsFromLevel({
      level,
      curves: profile.curves,
      density,
      sacaWeight,
    });

    if (fromLevel) {
      volume = fromLevel.volume ?? volume;
      if (fromLevel.weight != null) {
        weight = fromLevel.weight;
      }
    }
  }

  const safeSacaWeight = Number(sacaWeight) || 60;
  const settingsHasLowerCone = getHasLowerConeFromSettings(siloSettings);
  const dims = getSiloDimensionsFromCurves(profile.curves);
  const hasLowerCone = settingsHasLowerCone !== undefined
    ? settingsHasLowerCone
    : (dims ? dims.coneHeight > 0.05 : false);

  return {
    id: profile.id || sensorId,
    silo: profile.silo ?? siloPk ?? null,
    silo_name: profile.silo_name || siloNameFallback || 'Silo sem nome',
    food: food?.name || 'N/A',
    level,
    last_updated: profile.last_updated || profile.datetime_fetched,
    volume,
    weight,
    bags: safeSacaWeight > 0 ? (weight / safeSacaWeight) : 0,
    density,
    curves: profile.curves,
    useLevelMetrics,
    hasLowerCone,
    informedMaxWeight: Number(informedMaxWeight ?? profile.informed_max_weight) || 0,
  };
}
